import type { LayoutPlan, Page } from '../models/layout';
import type { LegalBlock, LegalRun } from '../models/ast';
import { getStylesRegistry } from '../styles/registry';
import { resolveRunStyle } from '../styles/resolveStyle';

export interface HtmlExportOptions {
  pageWidthPx: number;
  pageHeightPx: number;
  background?: string;
  fonts?: Array<{ family: string; url: string; weight?: number; style?: 'normal'|'italic' }>;
}

export function htmlFromLayoutPlan(plan: LayoutPlan, ast: LegalBlock[], opts: HtmlExportOptions): string {
  const styles = getStylesRegistry();
  const runById: Record<string, LegalRun> = {};
  const blockById: Record<string, any> = {} as any;
  for (const b of ast || []) {
    const anyB: any = b as any;
    blockById[anyB.id] = anyB;
    const children = (anyB.children || []) as any[];
    for (const ch of children) {
      if (ch && typeof ch === 'object' && 'id' in ch && 'text' in ch) runById[(ch as LegalRun).id] = ch as LegalRun;
      if (ch && (ch as any).children) {
        for (const nn of ((ch as any).children as any[])) {
          if (nn && typeof nn === 'object' && 'id' in nn && 'text' in nn) runById[(nn as LegalRun).id] = nn as LegalRun;
        }
      }
    }
  }

  function sliceGraphemes(text: string, start: number, endInclusive: number): string {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const seg = new (Intl as any).Segmenter(undefined, { granularity: 'grapheme' });
      const parts = Array.from(seg.segment(text)).map((s: any) => s.segment as string);
      return parts.slice(start, endInclusive + 1).join('');
    } catch {
      return text.slice(start, endInclusive + 1);
    }
  }

  const crossRefs: Array<{ sourceRunId: string; targetBlockId: string; targetPage?: number; status: string; display?: string }> = (plan as any).crossRefs || [];
  const byRunId = new Map<string, { href: string; display?: string }>();
  for (const cr of crossRefs) {
    if (cr.status !== 'resolved') continue;
    byRunId.set(cr.sourceRunId, { href: `#b_${cr.targetBlockId}`, display: cr.display });
  }

  function renderPage(page: Page): string {
    const bodyLines = page.body.lineBoxes || [];
    const header = `<div class="header" role="region" aria-label="header" style="position:absolute;left:${page.header.x}px;top:${page.header.y}px;width:${page.header.width}px;height:${page.header.height}px;"></div>`;
    const footer = `<div class="footer" role="contentinfo" aria-label="footer" style="position:absolute;left:${page.footer.x}px;top:${page.footer.y}px;width:${page.footer.width}px;height:${page.footer.height}px;"></div>`;

    // Group lines by blockId for semantic wrappers
    const groups: Record<string, typeof bodyLines> = {} as any;
    for (const lb of bodyLines) {
      (groups[lb.blockId] ||= [] as any).push(lb);
    }
    const blocksHtml = Object.entries(groups).map(([blockId, lines]) => {
      const block = blockById[blockId];
      const role = block?.type === 'Heading' ? 'heading' : block?.type === 'ListItem' ? 'listitem' : block?.type === 'List' ? 'list' : 'paragraph';
      const tag = block?.type === 'Heading' ? 'h2' : block?.type === 'ListItem' ? 'li' : block?.type === 'List' ? 'ul' : 'p';
      const aiAttrs = block?.aiAnnotations ? ` data-ai-annotations='${escapeHtml(JSON.stringify(block.aiAnnotations))}'` : '';
      const consAttrs = block?.legalConstraints ? ` data-constraints='${escapeHtml(JSON.stringify(block.legalConstraints))}'` : '';
      // Add named anchor for internal links
      const anchor = `<a name="b_${blockId}"></a>`;
      const wrapperStart = tag === 'ul' ? `<ul role="list" id="b_${blockId}"${aiAttrs}${consAttrs} style="position:relative;">${anchor}` : `<${tag} id="b_${blockId}" role="${role}"${aiAttrs}${consAttrs} style="position:relative;">${anchor}`;
      const wrapperEnd = tag === 'ul' ? `</ul>` : `</${tag}>`;
      let content = (lines as any[]).map((lb) => {
        const run = runById[lb.runId];
        let text = '';
        let family = 'serif';
        let sizePt = 12;
        let weight: number | undefined = undefined;
        let isItalic = false;
        if (run) {
          text = sliceGraphemes(run.text || '', lb.startGrapheme, lb.endGrapheme);
          const paragraphStyle = block?.styleId ? styles.paragraphStyles[block.styleId] : undefined;
          const rs: any = resolveRunStyle(paragraphStyle as any, run);
          family = rs?.fontFamily || 'serif';
          sizePt = typeof rs?.fontSizePt === 'number' ? rs.fontSizePt : 12;
          weight = rs?.fontWeight;
          isItalic = rs?.fontStyle === 'italic' || rs?.italic === true;
        }
        const left = lb.x;
        const top = lb.baseline - Math.round(sizePt * (96/72));
        const link = run && byRunId.get(run.id);
        const displayed = link ? `<a href="${link.href}" style="color:inherit;text-decoration:underline;">${link.display || text}</a>` : escapeHtml(text);
        const fw = typeof weight === 'number' ? `font-weight:${weight};` : '';
        const fs = isItalic ? 'font-style:italic;' : '';
        return `<span role="text" style="position:absolute;left:${left}px;top:${top}px;font-family:${family};font-size:${sizePt}pt;${fw}${fs}white-space:pre">${displayed}</span>`;
      }).join('');
      // Minimal image rendering for Image blocks
      if (block?.type === 'Image') {
        const imgSrc = block?.attrs?.src || '';
        const w = block?.attrs?.width ? Number(block.attrs.width) : 200;
        const h = block?.attrs?.height ? Number(block.attrs.height) : 150;
        const pos = Array.isArray(lines) && lines.length ? { left: lines[0].x, top: lines[0].y } : { left: 0, top: 0 };
        content += `<img alt="${escapeHtml(block?.attrs?.alt || '')}" src="${imgSrc}" style="position:absolute;left:${pos.left}px;top:${pos.top}px;width:${w}px;height:${h}px;object-fit:contain" />`;
      }
      return wrapperStart + content + wrapperEnd;
    }).join('');

    return `<section class="page" role="document" style="position:relative;width:${opts.pageWidthPx}px;height:${opts.pageHeightPx}px;background:${opts.background || '#fff'};margin:24px auto;border:1px solid #eee">${header}${footer}${blocksHtml}</section>`;
  }

  const pages = (plan.pages || []).map(renderPage).join('\n');
  const fontPreloads = (opts.fonts || [])
    .map((f) => `<link rel="preload" href="${f.url}" as="font" type="font/ttf" crossorigin>`)
    .join('');
  const fontFaces = (opts.fonts || [])
    .map((f) => `@font-face{font-family:'${f.family}';src:url('${f.url}') format('truetype');font-weight:${f.weight||400};font-style:${f.style||'normal'};font-display:swap;}`)
    .join('\n');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/><title>EdgeDocs Print</title>
  ${fontPreloads}
  <style>
    @page { size: ${opts.pageWidthPx}px ${opts.pageHeightPx}px; margin: 0; }
    body { background: #f8fafc; }
    ${fontFaces}
  </style>
  </head><body><main role="main">${pages}</main></body></html>`;
  return html;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}


